<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    class="message__body"
    :class="rootclass"
  >
    <button
      class="show-notes"
      title="View notes added by you or others"
      @click="listNotes"
    >
      <img
        v-if="noteExist"
        class="note-icon"
        src="/images/icons/view_notes.svg"
        title="View Notes"
      >
    </button>

    <note-buttons
      v-if="messageType === 'chat'"
      :noteId="message._id"
      :noteName="null"
      :noteLinked="message._id"
      :noteItem="'chat message'"
      :noteSiteId="message._source.site_id"
      :noteSiteName="message._source.site_name"
      :hasNotes="message._source.has_chat_message_notes"
      :indexType="indexType"
      :watchlistMessage="message"
      :siteLink="message._id"
      @refresh="refresh"
      @listNotes="listNotes"
    >
        <translate-button
            v-if="userHasTranslationPerm"
            slot="before-buttons"
            v-bind:docId="message._id"
            v-bind:translation-active="showTranslatedMessage"
            v-bind:haveTranslations="haveTranslations"
            @translationRequested="onTranslationRequested"
            @translationFinished="onTranslationFinished"
            @translationFailed="onTranslationFailed"
        />
    </note-buttons>

    <note-buttons
      v-else-if="messageType === 'forum'"
      :noteId="message._source.post_id"
      :noteName="message._source.title"
      :noteLinked="message._id"
      :noteSite="message._source.site_id"
      :noteItem="'post'"
      :noteSiteId="message._source.site_id"
      :noteSiteName="message._source.site_name"
      :hasNotes="message._source.has_post_notes"
      :indexType="indexType"
      :siteLink="siteLink"
      @refresh="refresh"
      @listNotes="listNotes"
    >
        <translate-button
            v-if="userHasTranslationPerm"
            slot="before-buttons"
            v-bind:docId="message._id"
            v-bind:translation-active="showTranslatedMessage"
            v-bind:haveTranslations="haveTranslations"
            @translationRequested="onTranslationRequested"
            @translationFinished="onTranslationFinished"
            @translationFailed="onTranslationFailed"
        />
    </note-buttons>

    <UserNote
      v-if="showUserNotes"
      :usernotes="usernotes"
      :indexType="indexType"
      :noteSiteName="message._source.site_name"
      :siteLink="siteLink"
      @listEmpty="listEmpty"
      @closed="showUserNotes = false"
      @add="addNoRefUserNote"
    />

    <template v-if="haveTranslations && showTranslatedMessage">
      <span class="translation-hint-note">
        <i><b>Note:</b> This is a best-effort AI translation of the message contents, therefore the translation may be inaccurate.</i>
      </span>
      <div style="margin-top: -16px;">&nbsp;</div>
    </template>
    <template v-if="translationError">
      <span class="translation-hint-note translation-error">
        <i><b>Error:</b> {{translationError}} </i>
      </span>
    </template>
    <template v-if="isLongPost">
      <div
        :data-long-post-hidden="!showLongPost"
        class="message__body-inner"
        v-html="sanitizedMessage"
      />
      <div
        class="message__body-tldr"
        :data-long-post-hidden="!showLongPost"
      >
        <button
          type="button"
          class="message__btn-tldr"
          @click.prevent="toggleLongPost"
        >
          <span class="btn btn--sm">
            {{ showLongPost ? gettext('Show less') : gettext('See full message') }}
          </span>
        </button>
      </div>
    </template>

    <template v-else-if="isKeywordPost">
      <div
        class="message__keywords"
        :data-long-post-hidden="!showLongPost"
      >
        <p class="u-mb-sm">
          <i>{{ gettext('Matching keywords in message:') }}</i>
        </p>
        <ul class="u-mb-0">
          <li
            v-for="(snippet, index) in message.highlight.message"
            :key="index"
            class="message__keyword-result"
          >
            <span v-html="snippet" />
          </li>
        </ul>
      </div>
      <div
        v-show="showKeywordPost"
        class="message__body-inner"
        v-html="sanitizedMessage"
      />
      <div
        class="message__body-tldr"
        :data-long-post-hidden="!showLongPost"
      >
        <button
          type="button"
          class="message__btn-tldr"
          @click.prevent="toggleKeywordPost"
        >
          <span class="btn btn--sm">
            {{ showKeywordPost ? gettext('Hide message') : gettext('See full message') }}
          </span>
        </button>
      </div>
    </template>
    <div
      v-else
      v-html="sanitizedMessage"
    />
  </div>
</template>

<script>
import NoteButtons from '../notes/NoteButtons'
import TranslateButton from "./TranslateButton.vue";
import UserNote from '../notes/UserNote'
import axios from 'axios'
import Cookies from 'js-cookie'
import {makeSourcesCopyable} from "../utils.js"


export default {
  name: 'MessageResultBody',
  components: {
    TranslateButton,
    NoteButtons,
    UserNote
  },
  props: {
    messageType: {
      type: String,
      default: ''
    },
    message: {
      type: Object,
      default: () => {}
    },
    featured: {
      type: Boolean,
      default: false
    },
    indexType: {
      type: String,
      default: ''
    },
    siteLink: {
      type: String,
      default: ''
    },
    hasNotes: {
      type: Boolean,
      default: null
    }
  },
  data() {
    return {
      isLongPost: false,
      isKeywordPost: false,
      showTranslatedMessage: false,
      showLongPost: false,
      showKeywordPost: false,
      note: '',
      noteType: '',
      noteCategory: '',
      noteExist: false,
      usernotes: [],
      showUserNotes: false,
      noteName: null,
      translatedMessage: null,
      translationError: null,
      userHasTranslationPerm: false,
    }
  },

  computed: {
    rootclass() {
      var classnames = []

      if (this.messageType === 'chat') {
        classnames.push('message__body--chat')
      } else {
        classnames.push('message__body--forum')
      }

      if (!this.featured) {
        classnames.push('u-fontsize-sm')
      }

      return classnames.join(' ')
    },
    noteItem() {
      let noteType = null
      if (this.messageType === 'chat') {
        noteType = 'chat message'
      } else {
        noteType = 'post'
      }
      return noteType
    },
    localizedMessage() {
      if (this.showTranslatedMessage) {
        // already existing translated message.
        if (this.message._source.translated_message) return this.message._source.translated_message
        // potentially newly created translated message.
        if (this.translatedMessage) return this.translatedMessage
      }
      return this.message._source.message
    },
    sanitizedMessage() {
      return this.makeSourcesCopyable(this.localizedMessage)
    },
    haveTranslations() {
      return !!(this.message._source.translated_message || this.translatedMessage)
    }
  },

  watch: {
    hasNotes() {
      this.noteExist = this.hasNotes
    },
  },

  mounted() {
    this.userHasTranslationPerm = !!window.can_translate_content_using_ai
    this.noteExist = this.hasNotes
    this.isLongPost = this.$el.clientHeight > 500 && !this.message.highlight

    this.isKeywordPost = this.$el.clientHeight > 500 && !!this.message.highlight && this.message.highlight.message.length > 0
    this.translatedMessage = this.message._source.translated_message
    // Copies link href or image source to clipboard, if target has it available
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.message__body')) return

      // need a bypass for this so that the notes dialog can render/operate correctly
      if (e.target.closest('.add-note-container') || e.target.closest('.edit-note-dialog')) return

      e.preventDefault()
      if (e.target.getAttribute('data-copy')) {
        navigator.clipboard.writeText(e.target.getAttribute('data-copy'))
      }
    })

    // The backend may have added some <div>s in this structure:
    //
    // <div class="c3p-encrypted">
    //   <div class="c3p-ciphertext">paihjfpqedirfhjpasdifhj</div>
    //   <div class="c3p-plaintext">This is the plaintext</div>
    // </div>
    //
    // By doing this CSS switcheroo, we can allow the user to toggle whether they want
    // to see the plaintext or ciphertext.  Normally they will want the plaintext, but
    // if the encryption detection code decrypts something that wasn't encrypted, they
    // will want to be able to recover the original content.
    for (let encryptedDiv of this.$el.getElementsByClassName('c3p-encrypted')) {
      encryptedDiv.setAttribute('title', 'Click to show encrypted/decrypted')
      encryptedDiv.addEventListener('click', (e) => {
        encryptedDiv.classList.toggle('show-ciphertext')
      })
    }
  },

  methods: {
    makeSourcesCopyable,
    toggleLongPost(e) {
      this.$emit('toggle-post', this.showLongPost)
      this.showLongPost = !this.showLongPost
    },

    toggleKeywordPost(e) {
      this.$emit('toggle-post', this.showKeywordPost)
      this.showKeywordPost = !this.showKeywordPost
    },

    refresh() {
      this.$emit('refresh')
    },
    listNotes() {
      const csrftoken = Cookies.get('csrftoken')
      this.showUserNotes = true
      let notes = {
        'choice_id': this.message._id,
        'name': this.noteName,
        'item': this.noteItem,
        'site_id': this.message._source.site_id,
        'site_name': this.message._source.site_name
      }
      axios.post('/en/message-search/notes/list/', notes, {'headers': {'X-CSRFToken': csrftoken}}).then(response => {
        this.usernotes = response.data.list_data
      })
    },
    addNoRefUserNote(value) {
      this.showInputFields = true
      this.showUserNotes = false
    },
    listEmpty() {
      this.noteExist = false
      this.usernotes = []
      this.$emit('refresh')
    },
    onTranslationRequested() {
      this.translationError = null
      this.showTranslatedMessage = !this.showTranslatedMessage
    },
    onTranslationFinished(data) {
      if (data && data.translation) {
        this.translatedMessage = data.translation
      }
    },
    onTranslationFailed(err) {
      if (err && err.response && err.response.data && err.response.data.detail) {
        this.translationError = err.response.data.detail
      }
    }
  },
}
</script>

<style>
.note-icon {
  width: 30px;
  height: auto;
}
.message__body .note-buttons {
  position: absolute;
  right: 10px;
}
.message__body .show-notes {
  position: absolute;
  right: 40px;
}
.message__body img.note-icon {
  width: 30px !important;
  height: auto !important;
  opacity: 1;
  border: 0;
}
div.content a {
  margin-right: 2px;
}
div.content a:after {
  background-image: url('/images/icons/content_copy48.svg');
  background-size: contain;
  background-repeat: no-repeat;
  background-position-y: 2px;
  display: inline-block;
  width: 20px;
  height: 20px;
  content: '';
  position: relative;
  right: -3px;
  top: 5px;
}

.translation-hint-note {
  text-decoration: underline;
  font-size: 14px;
  background-color: #dfdfdf;
  opacity: 0.7;
  padding: 8px;
  border-radius: 8px;
}
.translation-error {
  color: #a42334;
}
.message__body pre {  /* Fix overflow of code in <pre> tag */
  word-break: break-all;
}
</style>
